import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  api,
  type AdminLeague,
  type AdminLeagueWriteBody,
} from "../api/client";
import { ErrorBox, Loading } from "./Loading";

/**
 * Admin screen for listing leagues and creating/editing their planning data
 * (durationMonths + per-month Etapas). The league name remains the join key
 * with the existing importer flows.
 */
export function LeaguesAdminPanel() {
  const queryClient = useQueryClient();
  const leaguesQuery = useQuery<AdminLeague[]>({
    queryKey: ["admin", "leagues"],
    queryFn: api.adminLeagues,
  });

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: api.adminCreateLeague,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "leagues"] });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });
      queryClient.invalidateQueries({ queryKey: ["leagues", "all"] });
      setCreating(false);
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: number; body: Partial<AdminLeagueWriteBody> }) =>
      api.adminUpdateLeague(input.id, input.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "leagues"] });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });
      queryClient.invalidateQueries({ queryKey: ["leagues", "all"] });
      setEditingId(null);
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const leagues = leaguesQuery.data ?? [];
  const sorted = useMemo(
    () =>
      [...leagues].sort((a, b) => {
        if (a.startDate !== b.startDate) return a.startDate < b.startDate ? 1 : -1;
        return a.name.localeCompare(b.name);
      }),
    [leagues],
  );

  return (
    <section className="rounded-2xl border border-line bg-white p-6 space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Ligas
          </div>
          <h2 className="font-display text-xl font-bold text-ink">
            Ligas cadastradas
          </h2>
          <p className="text-[14px] leading-relaxed text-[#6b6356] mt-1">
            Cadastre o plano da liga (meses e etapas por mês) para que o ranking
            só descarte o pior evento de um mês depois que ele estiver completo.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => {
              setCreating(true);
              setEditingId(null);
              setErrorMsg(null);
            }}
            className="rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-semibold transition-colors"
          >
            Nova liga
          </button>
        )}
      </header>

      {errorMsg && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm flex items-start gap-3">
          <span className="flex-1">{errorMsg}</span>
          <button
            type="button"
            onClick={() => setErrorMsg(null)}
            className="text-red-500 hover:text-red-700 font-bold"
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}

      {creating && (
        <LeagueForm
          title="Nova liga"
          submitLabel="Cadastrar liga"
          submitting={createMutation.isPending}
          onCancel={() => {
            setCreating(false);
            setErrorMsg(null);
          }}
          onSubmit={(body) => createMutation.mutate(body)}
        />
      )}

      {leaguesQuery.isLoading ? (
        <Loading label="Carregando ligas..." />
      ) : leaguesQuery.error ? (
        <ErrorBox error={leaguesQuery.error} />
      ) : sorted.length === 0 ? (
        <p className="text-sm text-[#8a8170] italic">
          Nenhuma liga cadastrada ainda.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 py-2 font-bold">Liga</th>
                <th className="text-left px-3 py-2 font-bold w-44">Período</th>
                <th className="text-center px-3 py-2 font-bold w-24">Meses</th>
                <th className="text-left px-3 py-2 font-bold w-48">
                  Etapas / mês
                </th>
                <th className="text-right px-3 py-2 font-bold w-24">Eventos</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((league) =>
                editingId === league.id ? (
                  <tr key={league.id} className="border-t border-[#f3efe6]">
                    <td colSpan={6} className="px-3 py-3">
                      <LeagueForm
                        title={`Editar “${league.name}”`}
                        submitLabel="Salvar"
                        submitting={updateMutation.isPending}
                        initial={league}
                        onCancel={() => {
                          setEditingId(null);
                          setErrorMsg(null);
                        }}
                        onSubmit={(body) =>
                          updateMutation.mutate({ id: league.id, body })
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  <LeagueRow
                    key={league.id}
                    league={league}
                    onEdit={() => {
                      setCreating(false);
                      setEditingId(league.id);
                      setErrorMsg(null);
                    }}
                  />
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface LeagueRowProps {
  league: AdminLeague;
  onEdit: () => void;
}

function LeagueRow({ league, onEdit }: LeagueRowProps) {
  const monthsLabel = monthCountLabel(league.durationMonths);
  const etapasLabel = league.etapasPerMonth
    ? league.etapasPerMonth.join(" + ")
    : "—";
  const etapasTotal = league.etapasPerMonth
    ? league.etapasPerMonth.reduce((s, n) => s + n, 0)
    : null;
  return (
    <tr className="border-t border-[#f3efe6] hover:bg-[#fbfaf6] transition-colors">
      <td className="px-3 py-2.5 font-medium text-ink">{league.name}</td>
      <td className="px-3 py-2.5 text-[#6b6356] tabular-nums">
        {league.startDate} → {league.endDate}
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className="inline-block rounded-full bg-brand-50 text-brand-900 text-xs font-semibold px-2 py-0.5">
          {monthsLabel}
        </span>
      </td>
      <td className="px-3 py-2.5 text-[#6b6356] tabular-nums">
        {etapasLabel}
        {etapasTotal !== null && (
          <span className="text-[#9a9182] ml-1">(total {etapasTotal})</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[#6b6356]">
        {league.eventCount}
      </td>
      <td className="px-3 py-2.5 text-right">
        <button
          type="button"
          onClick={onEdit}
          className="text-sm font-semibold text-brand-700 hover:underline"
        >
          Editar
        </button>
      </td>
    </tr>
  );
}

interface LeagueFormProps {
  title: string;
  submitLabel: string;
  submitting: boolean;
  initial?: AdminLeague;
  onCancel: () => void;
  onSubmit: (body: AdminLeagueWriteBody) => void;
}

function LeagueForm({
  title,
  submitLabel,
  submitting,
  initial,
  onCancel,
  onSubmit,
}: LeagueFormProps) {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState(initial?.name ?? "");
  const [startDate, setStartDate] = useState(initial?.startDate ?? today);
  const [endDate, setEndDate] = useState(initial?.endDate ?? today);
  const [year, setYear] = useState(initial?.year ?? Number(today.slice(0, 4)));
  const [durationMonths, setDurationMonths] = useState(
    initial?.durationMonths ?? 1,
  );
  // Keep the array length in sync with durationMonths; default to 4 etapas per
  // month for new entries, which matches the most common Liga cadence.
  const [etapasPerMonth, setEtapasPerMonth] = useState<number[]>(
    initial?.etapasPerMonth ??
      Array.from({ length: initial?.durationMonths ?? 1 }, () => 4),
  );
  const [planEnabled, setPlanEnabled] = useState<boolean>(
    initial ? initial.etapasPerMonth !== null : true,
  );
  const [localError, setLocalError] = useState<string | null>(null);

  function handleDurationChange(next: number) {
    const months = Math.max(1, Math.min(24, Math.floor(next) || 1));
    setDurationMonths(months);
    setEtapasPerMonth((prev) => {
      if (prev.length === months) return prev;
      if (prev.length < months) {
        return [...prev, ...Array.from({ length: months - prev.length }, () => 4)];
      }
      return prev.slice(0, months);
    });
  }

  function handleEtapaChange(idx: number, value: string) {
    const n = Math.max(0, Math.min(99, Math.floor(Number(value) || 0)));
    setEtapasPerMonth((prev) => prev.map((v, i) => (i === idx ? n : v)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!name.trim()) {
      setLocalError("Nome é obrigatório.");
      return;
    }
    if (startDate > endDate) {
      setLocalError("Data de início deve ser anterior ou igual à data de fim.");
      return;
    }
    if (planEnabled && etapasPerMonth.length !== durationMonths) {
      setLocalError(
        `Plano de etapas precisa ter ${durationMonths} valor(es), um por mês.`,
      );
      return;
    }
    onSubmit({
      name: name.trim(),
      year,
      startDate,
      endDate,
      durationMonths,
      etapasPerMonth: planEnabled ? etapasPerMonth : null,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-brand-100 bg-brand-50/40 p-4 space-y-4"
    >
      <h3 className="font-display font-bold text-ink">{title}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Nome">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Liga Abr/Mai 2026"
            required
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </Field>
        <Field label="Ano">
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value) || year)}
            min={2000}
            max={2100}
            required
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </Field>
        <Field label="Data de início">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </Field>
        <Field label="Data de fim">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </Field>
        <Field label="Quantidade de meses">
          <input
            type="number"
            value={durationMonths}
            onChange={(e) => handleDurationChange(Number(e.target.value))}
            min={1}
            max={24}
            required
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </Field>
      </div>

      <div className="rounded-lg border border-brand-100 bg-white p-3 space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-ink cursor-pointer">
          <input
            type="checkbox"
            checked={planEnabled}
            onChange={(e) => setPlanEnabled(e.target.checked)}
          />
          Definir quantidade de etapas por mês
        </label>
        {planEnabled ? (
          <>
            <p className="text-xs text-[#6b6356] leading-relaxed">
              Informe quantas etapas estão previstas em cada mês. Enquanto o mês
              não tiver atingido essa quantidade, o ranking não descarta o pior
              evento desse mês (evitando que um evento futuro com 0 pontos
              prejudique todos os jogadores).
            </p>
            <div className="flex flex-wrap gap-3">
              {etapasPerMonth.map((value, idx) => (
                <label
                  key={idx}
                  className="flex items-center gap-1.5 text-sm text-[#6b6356]"
                >
                  <span className="font-medium text-ink">Mês {idx + 1}:</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={value}
                    onChange={(e) => handleEtapaChange(idx, e.target.value)}
                    className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </label>
              ))}
            </div>
            <p className="text-xs text-[#8a8170]">
              Total previsto:{" "}
              <strong className="text-ink">
                {etapasPerMonth.reduce((s, n) => s + n, 0)} etapas
              </strong>
            </p>
          </>
        ) : (
          <p className="text-xs text-[#6b6356]">
            Sem plano definido: o ranking usa o comportamento antigo (descarta o
            pior evento de cada mês com 2+ eventos, mesmo que o mês ainda não
            esteja completo). Recomendado apenas para ligas já encerradas.
          </p>
        )}
      </div>

      {localError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {localError}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-brand-700 hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 text-sm font-semibold transition-colors"
        >
          {submitting ? "Salvando..." : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 text-sm font-semibold text-[#6b6356] transition-colors"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-[#6b6356] mb-1 font-medium">{label}</span>
      {children}
    </label>
  );
}

function monthCountLabel(months: number): string {
  if (months === 1) return "1 mês";
  return `${months} meses`;
}
